import type { ReactElement } from "react"
import { useTranslation } from "react-i18next"

import {
  GridActionTooltipButton,
  GridActionTooltipLink,
  baseColumnDef,
} from "@/nzds/data-grid"

import DeleteIcon from "@mui/icons-material/Delete"
import EditIcon from "@mui/icons-material/Edit"
import VisibilityIcon from "@mui/icons-material/Visibility"
import type {
  GridActionsCellItemProps,
  GridColDef,
  GridRowId,
  GridRowParams,
} from "@mui/x-data-grid-pro"
import { GridActionsCellItem } from "@mui/x-data-grid-pro"

/**
 * Shared "View" grid action used by admin action columns.
 */
export function createAdminGridViewAction(
  to: string,
  label: string
): ReactElement<GridActionsCellItemProps> {
  return (
    <GridActionsCellItem
      component={GridActionTooltipLink}
      icon={<VisibilityIcon color="primary" />}
      key="view-action"
      label={label}
      showInMenu={false}
      // @ts-expect-error GridActionsCellItem does not type Link's `to` prop
      to={to}
    />
  )
}

/**
 * Shared "Edit" grid action used by admin action columns.
 */
export function createAdminGridEditAction(
  to: string,
  label: string
): ReactElement<GridActionsCellItemProps> {
  return (
    <GridActionsCellItem
      component={GridActionTooltipLink}
      icon={<EditIcon color="primary" />}
      key="edit-action"
      label={label}
      showInMenu={false}
      // @ts-expect-error GridActionsCellItem does not type Link's `to` prop
      to={to}
    />
  )
}

/**
 * Shared "Delete" grid action used by admin action columns.
 */
export function createAdminGridDeleteAction({
  disabled = false,
  label,
  onDeleteClick,
}: {
  disabled?: boolean
  label: string
  onDeleteClick: () => void
}): ReactElement<GridActionsCellItemProps> {
  return (
    <GridActionsCellItem
      component={GridActionTooltipButton}
      disabled={disabled}
      icon={<DeleteIcon />}
      key="delete-action"
      label={label}
      onClick={onDeleteClick}
      showInMenu={false}
    />
  )
}

interface UseAdminGridActionsColumnOptions<TRow extends { id: GridRowId }> {
  getActions: (
    params: GridRowParams<TRow>
  ) => ReactElement<GridActionsCellItemProps>[]
  minWidth?: number
  width?: number
}

/**
 * Thin actions-column chrome for admin grids. Callers compose row actions with
 * createAdminGridViewAction / createAdminGridEditAction / createAdminGridDeleteAction.
 */
export function useAdminGridActionsColumn<TRow extends { id: GridRowId }>({
  getActions,
  minWidth = 100,
  width = 100,
}: UseAdminGridActionsColumnOptions<TRow>): GridColDef<TRow> {
  const { t } = useTranslation("common")

  return {
    ...baseColumnDef,
    align: "left",
    cellClassName: "actions",
    field: "actions",
    getActions,
    headerAlign: "left",
    headerName: t("gridColumn.actions.name"),
    minWidth,
    type: "actions",
    width,
  }
}
